import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { KeyRound, Eye, EyeOff, Copy, Webhook, Zap, CheckCircle2, AlertCircle, Activity, RefreshCw, DollarSign } from "lucide-react";
import { toast } from "sonner";
import AdminDeleteConversionButton from "@/components/AdminDeleteConversionButton";
import ConfirmDialog from "@/components/ConfirmDialog";

export default function AdminIntegrationsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [showKey, setShowKey] = useState(false);

  // Test webhook panel state
  const [testRef, setTestRef] = useState("");
  const [testTotal, setTestTotal] = useState("100");
  const [testCurrency, setTestCurrency] = useState("USD");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);

  // Live webhook log viewer state
  const [logs, setLogs] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Manual credit panel state — for crediting an affiliate when the partner
  // site's webhook failed to fire (cookie bug, downtime, etc).
  const [mcRef, setMcRef] = useState("");
  const [mcOrderId, setMcOrderId] = useState("");
  const [mcTotal, setMcTotal] = useState("");
  const [mcCurrency, setMcCurrency] = useState("GYD");
  const [mcPct, setMcPct] = useState("10");
  const [mcCrediting, setMcCrediting] = useState(false);
  const [mcResult, setMcResult] = useState(null);
  const [mcConfirm, setMcConfirm] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/integrations/key");
        setData(data);
      } catch {
        setData({ configured: false, api_key: null });
      }
    })();
  }, []);

  const loadLogs = React.useCallback(async () => {
    setLogsLoading(true);
    try {
      const { data: resp } = await api.get("/admin/integrations/logs?limit=50");
      setLogs(resp.logs || []);
    } catch {
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(loadLogs, 8000);
    return () => clearInterval(t);
  }, [autoRefresh, loadLogs]);

  const runTest = async (e) => {
    e?.preventDefault?.();
    setTestError(null);
    setTestResult(null);
    const ref = testRef.trim();
    if (!ref) {
      toast.error("Enter an affiliate slug (ref) to test");
      return;
    }
    const totalNum = parseFloat(testTotal);
    if (!Number.isFinite(totalNum) || totalNum < 0) {
      toast.error("Enter a valid order total");
      return;
    }
    setTesting(true);
    try {
      const { data: resp } = await api.post("/admin/integrations/test-conversion", {
        ref,
        total: totalNum,
        currency: (testCurrency || "USD").trim().toUpperCase(),
        commission_pct: 10,
      });
      setTestResult(resp);
      toast.success("Webhook dry-run succeeded");
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail) || err.message;
      setTestError(detail || "Test failed");
      toast.error(detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const submitManualCredit = (e) => {
    e?.preventDefault?.();
    setMcResult(null);
    if (!mcRef.trim()) { toast.error("Enter the affiliate slug (ref)"); return; }
    if (!mcOrderId.trim()) { toast.error("Enter the order id"); return; }
    const total = parseFloat(mcTotal);
    if (!Number.isFinite(total) || total < 0) { toast.error("Enter a valid order total"); return; }
    // Always go through a confirmation so a typo can't accidentally credit
    // the wrong affiliate for a real-money amount.
    setMcConfirm(true);
  };

  const doManualCredit = async () => {
    setMcCrediting(true);
    try {
      const { data: resp } = await api.post("/admin/integrations/manual-credit", {
        ref: mcRef.trim(),
        order_id: mcOrderId.trim(),
        total: parseFloat(mcTotal),
        currency: (mcCurrency || "GYD").trim().toUpperCase(),
        commission_pct: parseFloat(mcPct) || 10,
      });
      setMcResult(resp);
      if (resp.duplicate) {
        toast.info("This order was already credited earlier (idempotent)");
      } else {
        toast.success(`Credited ${resp.credited_to_balance.toFixed(2)} ${resp.currency || "GYD"}`);
        // Clear inputs so admin doesn't accidentally double-submit
        setMcOrderId("");
        setMcTotal("");
      }
      setMcConfirm(false);
      // Refresh the log so the manual credit shows up live
      loadLogs();
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail) || err.message;
      toast.error(detail || "Credit failed");
    } finally {
      setMcCrediting(false);
    }
  };

  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const key = data?.api_key || "";
  const masked = key ? key.slice(0, 4) + "•".repeat(Math.max(0, key.length - 8)) + key.slice(-4) : "";
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

  // Build a "ready to paste" cURL using the slug + currency the admin typed
  // into the test panel, and the real API key if it's been revealed. The
  // partner gets a single-block copy that already targets their slug.
  const liveRef = testRef.trim() || "ada-lovelace-x7k2qz";
  const liveCurrency = (testCurrency.trim() || "GYD").toUpperCase();
  const liveKey = showKey && key ? key : "$TRACKMATRIX_API_KEY";
  const curlExample =
`curl -X POST ${baseUrl}/api/conversion \\
  -H "Authorization: Bearer ${liveKey}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "ref": "${liveRef}",
    "order_id": "ord_abc123",
    "total": 62.00,
    "currency": "${liveCurrency}",
    "items_count": 2,
    "occurred_at": "2026-02-29T18:00:00Z"
  }'`;

  return (
    <DashboardLayout>
      <div className="mb-8">
        <div className="label-eyebrow text-[#002FA7] flex items-center gap-2">
          <Webhook size={12} /> Admin · Integrations
        </div>
        <h1 className="text-4xl sm:text-5xl tracking-tight font-black text-[#111827]">Conversion webhook</h1>
        <p className="text-sm text-[#4B5563] mt-2 max-w-2xl">
          Send purchase events from your e-commerce site (e.g. <span className="font-mono">guyanadiscounts.com</span>) to trackmatrix so every link's <span className="font-bold">Conv.</span> column reflects real revenue, not just leads.
        </p>
      </div>

      {/* API key */}
      <div className="card-flat p-6 mb-6" data-testid="integration-api-key-card">
        <div className="label-eyebrow text-[#4B5563] mb-2 flex items-center gap-2"><KeyRound size={12} /> API key</div>
        {data === null ? (
          <div className="h-10 bg-[#F9FAFB] rounded animate-pulse" />
        ) : !data.configured ? (
          <div className="text-sm text-[#92400E] bg-[#FEF3C7] border border-[#FDE68A] rounded-md p-3">
            No <span className="font-mono">TRACKMATRIX_API_KEY</span> is configured on the server. Set it as an environment variable on the backend and restart, then refresh this page.
          </div>
        ) : (
          <div className="flex items-center gap-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-md px-3 py-2 font-mono text-sm" data-testid="integration-api-key-value">
            <span className="flex-1 truncate select-all">{showKey ? key : masked}</span>
            <button
              onClick={() => setShowKey((v) => !v)}
              data-testid="integration-toggle-key"
              className="text-[#4B5563] hover:text-[#111827] p-1.5 rounded"
              title={showKey ? "Hide" : "Reveal"}
            >
              {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
            <button
              onClick={() => copyToClipboard(key, "API key copied")}
              data-testid="integration-copy-key"
              className="text-[#4B5563] hover:text-[#002FA7] p-1.5 rounded"
              title="Copy"
            >
              <Copy size={14} />
            </button>
          </div>
        )}
        <p className="text-xs text-[#4B5563] mt-3 leading-relaxed">
          Paste this into the destination site's environment as <span className="font-mono bg-[#F3F4F6] px-1 rounded">TRACKMATRIX_API_KEY</span> and send it as <span className="font-mono bg-[#F3F4F6] px-1 rounded">Authorization: Bearer …</span> on every conversion POST. Treat it like a password — anyone with this key can record conversions for any affiliate slug.
        </p>
      </div>

      {/* Endpoint reference */}
      <div className="card-flat p-6 mb-6">
        <div className="label-eyebrow text-[#4B5563] mb-2">Endpoint</div>
        <div className="font-mono text-sm text-[#111827] flex items-center gap-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-md px-3 py-2" data-testid="integration-endpoint">
          <span className="text-[#9CA3AF]">POST</span>
          <span className="flex-1 truncate">{baseUrl}/api/conversion</span>
          <button
            onClick={() => copyToClipboard(`${baseUrl}/api/conversion`, "Endpoint copied")}
            data-testid="integration-copy-endpoint"
            className="text-[#4B5563] hover:text-[#002FA7] p-1.5 rounded"
            title="Copy"
          >
            <Copy size={14} />
          </button>
        </div>
      </div>

      {/* Curl example */}
      <div className="card-flat p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="label-eyebrow text-[#4B5563]">cURL example</div>
          <button
            onClick={() => copyToClipboard(curlExample.replace("$TRACKMATRIX_API_KEY", key || "$TRACKMATRIX_API_KEY"), "Example copied")}
            data-testid="integration-copy-curl"
            className="text-xs font-bold text-[#002FA7] inline-flex items-center gap-1 hover:underline"
          >
            <Copy size={12} /> Copy
          </button>
        </div>
        <pre className="bg-[#0B1220] text-[#E5E7EB] rounded-md p-4 overflow-x-auto text-xs leading-relaxed" data-testid="integration-curl-example">
{curlExample}
        </pre>
        <ul className="text-xs text-[#4B5563] mt-3 space-y-1 leading-relaxed">
          <li>• <span className="font-bold">ref</span> — the slug from <span className="font-mono">trackmatrix.net/r/&lt;slug&gt;</span>. The destination URL receives it as <span className="font-mono">?ref=&lt;slug&gt;</span>.</li>
          <li>• <span className="font-bold">order_id</span> — your shop's order ID. Posting the same <span className="font-mono">(ref, order_id)</span> twice is a no-op (idempotent, returns 200).</li>
          <li>• <span className="font-bold">total</span> — order total in any currency you specify.</li>
          <li>• <span className="font-bold">occurred_at</span> — ISO timestamp of the purchase (optional, defaults to now).</li>
        </ul>
      </div>

      {/* Test webhook (dry-run) */}
      <div className="card-flat p-6 mt-6" data-testid="test-webhook-card">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="label-eyebrow text-[#4B5563] flex items-center gap-2"><Zap size={12} /> Test webhook</div>
            <p className="text-xs text-[#4B5563] mt-1 max-w-xl leading-relaxed">
              Dry-run validation against a real affiliate slug. Confirms the server key is set, the slug resolves, and the 10% commission math is right. <span className="font-bold">No conversion is recorded and no balance is credited</span> — safe to spam.
            </p>
          </div>
        </div>

        <form onSubmit={runTest} className="grid grid-cols-1 sm:grid-cols-[1fr_140px_120px_auto] gap-2 items-stretch">
          <input
            data-testid="test-webhook-ref-input"
            value={testRef}
            onChange={(e) => setTestRef(e.target.value)}
            placeholder="affiliate slug (e.g. ada-lovelace-x7k2qz)"
            className="rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-sm font-mono text-[#111827] outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]"
          />
          <input
            data-testid="test-webhook-total-input"
            type="number"
            min="0"
            step="0.01"
            value={testTotal}
            onChange={(e) => setTestTotal(e.target.value)}
            placeholder="total"
            className="rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-sm font-mono text-[#111827] outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]"
          />
          <input
            data-testid="test-webhook-currency-input"
            value={testCurrency}
            onChange={(e) => setTestCurrency(e.target.value.toUpperCase().slice(0, 6))}
            placeholder="USD"
            className="rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-sm font-mono text-[#111827] outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]"
          />
          <button
            type="submit"
            disabled={testing || !data?.configured}
            data-testid="test-webhook-submit-button"
            className="btn-accent inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {testing ? "Testing…" : <>Send test <Zap size={14} /></>}
          </button>
        </form>

        {!data?.configured && (
          <p className="text-xs text-[#92400E] bg-[#FEF3C7] border border-[#FDE68A] rounded-md p-2 mt-3" data-testid="test-webhook-no-key-warning">
            Set <span className="font-mono">TRACKMATRIX_API_KEY</span> on the backend before running a test.
          </p>
        )}

        {testResult && (
          <div className="mt-4 rounded-md border border-[#A7F3D0] bg-[#ECFDF5] p-4" data-testid="test-webhook-success">
            <div className="flex items-start gap-2">
              <CheckCircle2 size={18} className="text-[#047857] flex-shrink-0 mt-0.5" />
              <div className="text-sm text-[#065F46] leading-relaxed flex-1 min-w-0">
                <div className="font-bold text-[#047857]">Dry-run OK</div>
                <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs font-mono">
                  <div><span className="text-[#065F46]/70">ref:</span> {testResult.ref}</div>
                  <div><span className="text-[#065F46]/70">link:</span> {testResult.link_name || "—"}</div>
                  <div className="truncate"><span className="text-[#065F46]/70">affiliate:</span> {testResult.affiliate_email || "—"}</div>
                  <div><span className="text-[#065F46]/70">would credit:</span> <span className="font-bold">{testResult.would_credit?.toFixed(2)} {testResult.currency}</span></div>
                  <div><span className="text-[#065F46]/70">commission %:</span> {testResult.commission_pct}</div>
                  <div><span className="text-[#065F46]/70">order_id:</span> {testResult.order_id}</div>
                </div>
                <p className="text-xs text-[#047857] mt-2">{testResult.message}</p>
              </div>
            </div>
          </div>
        )}

        {testError && (
          <div className="mt-4 rounded-md border border-[#FCA5A5] bg-[#FEF2F2] p-4" data-testid="test-webhook-error">
            <div className="flex items-start gap-2">
              <AlertCircle size={18} className="text-[#B91C1C] flex-shrink-0 mt-0.5" />
              <div className="text-sm text-[#7F1D1D] leading-relaxed">
                <div className="font-bold text-[#B91C1C]">Test failed</div>
                <div className="text-xs font-mono mt-1 break-all">{testError}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Manual-credit panel — for crediting an affiliate when the partner
          site failed to fire its webhook (cookie bug, downtime, etc). */}
      <div className="card-flat p-6 mt-6" data-testid="manual-credit-card">
        <div className="mb-4">
          <div className="label-eyebrow text-[#4B5563] flex items-center gap-2">
            <DollarSign size={12} /> Manually credit a conversion
          </div>
          <p className="text-xs text-[#4B5563] mt-1 max-w-2xl leading-relaxed">
            When a partner site (e.g. <span className="font-mono">guyanadiscounts.com</span>) failed to fire the webhook for a real order — wrong cookie, downtime, bug — paste the affiliate's slug + the partner's order id here and credit them directly. <span className="font-bold">Idempotent</span>: re-submitting the same (ref, order_id) is a no-op. The credit shows up in the log below as a normal <span className="font-mono">ok</span> row.
          </p>
        </div>

        <form onSubmit={submitManualCredit} className="grid grid-cols-1 sm:grid-cols-[1.2fr_1.2fr_100px_90px_80px_auto] gap-2">
          <input
            data-testid="manual-credit-ref-input"
            value={mcRef}
            onChange={(e) => setMcRef(e.target.value)}
            placeholder="affiliate slug (e.g. mr-5dc2fc)"
            className="rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-sm font-mono text-[#111827] outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]"
          />
          <input
            data-testid="manual-credit-order-input"
            value={mcOrderId}
            onChange={(e) => setMcOrderId(e.target.value)}
            placeholder="order id (e.g. ord_60c63c5d0294)"
            className="rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-sm font-mono text-[#111827] outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]"
          />
          <input
            data-testid="manual-credit-total-input"
            type="number"
            min="0"
            step="0.01"
            value={mcTotal}
            onChange={(e) => setMcTotal(e.target.value)}
            placeholder="total"
            className="rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-sm font-mono text-[#111827] outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]"
          />
          <input
            data-testid="manual-credit-currency-input"
            value={mcCurrency}
            onChange={(e) => setMcCurrency(e.target.value.toUpperCase().slice(0, 6))}
            placeholder="GYD"
            className="rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-sm font-mono text-[#111827] outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]"
          />
          <input
            data-testid="manual-credit-pct-input"
            type="number"
            min="0"
            max="100"
            step="0.5"
            value={mcPct}
            onChange={(e) => setMcPct(e.target.value)}
            placeholder="10"
            title="Commission % (default 10)"
            className="rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-sm font-mono text-[#111827] outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]"
          />
          <button
            type="submit"
            disabled={mcCrediting}
            data-testid="manual-credit-submit-button"
            className="inline-flex items-center justify-center gap-1.5 bg-[#047857] hover:bg-[#065F46] text-white font-bold text-sm px-4 py-2 rounded-md disabled:opacity-50"
          >
            {mcCrediting ? "Crediting…" : <>Credit <DollarSign size={14} /></>}
          </button>
        </form>

        <p className="text-[10px] text-[#9CA3AF] mt-2 font-mono">
          Columns: slug · order id · total · currency · commission % · action
        </p>

        {mcResult && (
          <div className="mt-4 rounded-md border border-[#A7F3D0] bg-[#ECFDF5] p-4" data-testid="manual-credit-success">
            <div className="flex items-start gap-2">
              <CheckCircle2 size={18} className="text-[#047857] flex-shrink-0 mt-0.5" />
              <div className="text-sm text-[#065F46] leading-relaxed flex-1 min-w-0">
                <div className="font-bold text-[#047857]">
                  {mcResult.duplicate ? "Already credited earlier (idempotent — no double-credit)" : "Credit posted to affiliate"}
                </div>
                <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs font-mono">
                  <div><span className="text-[#065F46]/70">conversion_id:</span> {mcResult.conversion_id}</div>
                  <div><span className="text-[#065F46]/70">credited:</span> <span className="font-bold">{(mcResult.credited_to_balance || 0).toFixed(2)} {mcResult.currency || mcCurrency}</span></div>
                  {mcResult.affiliate_link_name && (
                    <div className="col-span-2"><span className="text-[#065F46]/70">link:</span> {mcResult.affiliate_link_name}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Live webhook log viewer */}
      <div className="card-flat p-6 mt-6" data-testid="webhook-logs-card">
        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <div>
            <div className="label-eyebrow text-[#4B5563] flex items-center gap-2">
              <Activity size={12} /> Recent webhook calls
              {autoRefresh && <span className="inline-flex h-1.5 w-1.5 rounded-full bg-[#10B981] animate-pulse" />}
            </div>
            <p className="text-xs text-[#4B5563] mt-1 max-w-xl leading-relaxed">
              Live tail of inbound <span className="font-mono">POST /api/conversion</span> requests. Refreshes every 8s while open.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setAutoRefresh((v) => !v)}
              data-testid="webhook-logs-toggle-auto"
              className="text-xs font-bold text-[#4B5563] border border-[#E5E7EB] rounded-md px-3 py-1.5 hover:border-[#111827]"
            >
              {autoRefresh ? "Pause" : "Resume"}
            </button>
            <button
              type="button"
              onClick={loadLogs}
              data-testid="webhook-logs-refresh"
              className="text-xs font-bold text-[#002FA7] inline-flex items-center gap-1 border border-[#E5E7EB] rounded-md px-3 py-1.5 hover:border-[#002FA7]"
            >
              <RefreshCw size={12} className={logsLoading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>

        {logs === null ? (
          <div className="h-24 bg-[#F9FAFB] rounded animate-pulse" />
        ) : logs.length === 0 ? (
          <div className="text-sm text-[#6B7280] bg-[#F9FAFB] border border-dashed border-[#E5E7EB] rounded-md p-6 text-center" data-testid="webhook-logs-empty">
            No webhook calls yet. Once your partner site posts to <span className="font-mono">/api/conversion</span>, calls show up here within ~8s.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2" data-testid="webhook-logs-list">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[#9CA3AF] uppercase tracking-wide">
                  <th className="px-2 py-2 font-bold">When</th>
                  <th className="px-2 py-2 font-bold">Status</th>
                  <th className="px-2 py-2 font-bold">Ref</th>
                  <th className="px-2 py-2 font-bold">Order</th>
                  <th className="px-2 py-2 font-bold text-right">Total</th>
                  <th className="px-2 py-2 font-bold text-right">Credited</th>
                  <th className="px-2 py-2 font-bold">IP</th>
                  <th className="px-2 py-2 font-bold text-right w-10"></th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <LogRow key={log.log_id} log={log} onConversionDeleted={loadLogs} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirm manual credit — gate the action behind a confirmation since
          it directly mutates an affiliate's real-money balance. */}
      <ConfirmDialog
        open={mcConfirm}
        tone="primary"
        icon={DollarSign}
        title={`Credit ${mcRef.trim()} for order ${mcOrderId.trim()}?`}
        description={
          (() => {
            const t = parseFloat(mcTotal) || 0;
            const pct = parseFloat(mcPct) || 10;
            const credit = (t * pct / 100).toFixed(2);
            return `Total ${t.toFixed(2)} ${mcCurrency} × ${pct}% commission = ${credit} ${mcCurrency} will be added to this affiliate's balance. This action is idempotent — re-submitting the same (ref, order_id) won't double-credit. Use this only when the partner site failed to fire its webhook for a real order.`;
          })()
        }
        confirmLabel="Yes, credit this affiliate"
        busy={mcCrediting}
        busyLabel="Crediting…"
        onCancel={() => !mcCrediting && setMcConfirm(false)}
        onConfirm={doManualCredit}
      />
    </DashboardLayout>
  );
}

const STATUS_STYLES = {
  ok: "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]",
  duplicate: "bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]",
  unauthorized: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  not_found: "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]",
  bad_request: "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]",
  server_error: "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]",
};

function _formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function LogRow({ log, onConversionDeleted }) {
  const style = STATUS_STYLES[log.status] || "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]";
  // Only `ok` rows actually created a conversion record we can delete.
  // `duplicate` rows reference an existing conversion_id (created earlier) —
  // deleting that would also work and IS what an admin probably wants when
  // chasing a refund, so we allow it there too. Other statuses (unauthorized
  // / not_found / errors) never wrote a conversion, so no button.
  const canDelete = !!log.conversion_id && (log.status === "ok" || log.status === "duplicate");
  return (
    <tr className="border-t border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`webhook-log-row-${log.log_id}`}>
      <td className="px-2 py-2 text-[#4B5563] whitespace-nowrap" title={log.created_at}>{_formatWhen(log.created_at)}</td>
      <td className="px-2 py-2">
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${style}`}>
          {log.status}
        </span>
      </td>
      <td className="px-2 py-2 font-mono text-[#111827] truncate max-w-[200px]" title={log.ref || ""}>{log.ref || "—"}</td>
      <td className="px-2 py-2 font-mono text-[#4B5563] truncate max-w-[180px]" title={log.order_id || ""}>{log.order_id || "—"}</td>
      <td className="px-2 py-2 font-mono text-[#111827] text-right whitespace-nowrap">{log.total != null ? `${log.total.toFixed(2)} ${log.currency || ""}` : "—"}</td>
      <td className="px-2 py-2 font-mono text-[#047857] text-right whitespace-nowrap font-bold">{log.credited != null ? log.credited.toFixed(2) : "—"}</td>
      <td className="px-2 py-2 font-mono text-[#9CA3AF] whitespace-nowrap">{log.source_ip || "—"}</td>
      <td className="px-2 py-2 text-right">
        {canDelete && (
          <AdminDeleteConversionButton
            conversion={{
              conversion_id: log.conversion_id,
              order_id: log.order_id,
              commission_total: log.credited,
              currency: log.currency,
            }}
            onDeleted={onConversionDeleted}
          />
        )}
      </td>
    </tr>
  );
}
